import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import en from "@/i18n/en";
import nl from "@/i18n/nl";

const DICTS = { en, nl };
const LANG_KEY = "ic_lang";

const I18nContext = createContext(null);

function getPath(obj, path) {
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

function interpolate(str, vars) {
  if (typeof str !== "string" || !vars) return str;
  return str.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => (vars[k] ?? ""));
}

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      const s = localStorage.getItem(LANG_KEY);
      if (s === "en" || s === "nl") return s;
      // Default to Dutch; fall back to English only when the browser is clearly English.
      const nav = (typeof navigator !== "undefined" && (navigator.language || "")).toLowerCase();
      if (nav.startsWith("en")) return "en";
    } catch {}
    return "nl";
  });

  useEffect(() => {
    try {
      localStorage.setItem(LANG_KEY, lang);
      document.documentElement.setAttribute("lang", lang);
    } catch {}
  }, [lang]);

  const t = useCallback(
    (key, vars) => {
      const dict = DICTS[lang] || DICTS.en;
      const val = getPath(dict, key);
      if (val == null) {
        const fallback = getPath(DICTS.en, key);
        return interpolate(fallback ?? key, vars);
      }
      return interpolate(val, vars);
    },
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};

export const useT = () => useI18n().t;
